// THE PUBLIC NAV — the bar every logged-out page of the TENANT app wears.
//
// It used to live inside pages/homePage/Home.jsx. It was lifted out on
// 22 Sep 2026, when the four static pages (/about /terms /privacy /contact)
// arrived: two copies of a nav drift, and the copy on the legal pages is
// exactly the one nobody looks at afterwards. Home and PublicPage now render
// the same component, so they cannot disagree.
//
// WHAT IS DELIBERATE HERE
//
//  · SIGN IN IS ONE BUTTON to this app's login (owner, 1 Oct 2026 — the old
//    patient/tenant chooser asked a question the provider app should not).
//    Patients get the separate "For patients" link to their own site, shown
//    only when this build knows it (`VITE_PATIENT_WEB_URL`).
//  · SECTION LINKS ARE NOT HREFS. This app is a HashRouter SPA: a real
//    `#solutions` in the address bar would be read as a ROUTE. Every section
//    link preventDefaults and scrolls instead — and from a static page, where
//    there is no such section, it navigates home carrying `state.scrollTo`,
//    which Home performs once it has mounted.
//  · "Get started" only exists when a DOCTOR can register somewhere: patients
//    never register on this app (owner, 1 Oct 2026), so /register-choice is the
//    doctor's door alone and bounces home when no organisation takes sign-ups.

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Languages, Menu, X, ArrowUpRight } from 'lucide-react';
import { getLanguages } from '../../redux/translationSlice';
import { PATIENT_WEB_URL } from '../../utils/patientWebUrl';
import useDoctorRegistrationOpen from '../../hooks/useDoctorRegistrationOpen';
import logo from '../../assets/img/logo-TT2.png';

// The anchors Home renders, in nav order. `ids` is a LIST because the pricing
// band is two elements: <LandingPackages/> owns `#pricing` and hides itself
// when the platform sells no public plan, in which case the custom-bundle card
// below it is what "Pricing" should land on.
// Not exported: nothing else needs it, and a non-component export here costs
// this file its fast-refresh boundary.
const PUBLIC_SECTIONS = [
  { id: 'solutions', ids: ['solutions'], labelKey: 'home.nav_solutions' },
  { id: 'services', ids: ['services'], labelKey: 'home.nav_services' },
  { id: 'features', ids: ['features'], labelKey: 'home.nav_features' },
  { id: 'pricing', ids: ['pricing', 'custom-plan'], labelKey: 'home.nav_pricing' },
];

// jsdom implements neither scrollIntoView nor matchMedia fully, and a nav link
// clicked in a test must not throw. Decoration only.
const scrollToFirst = (ids) => {
  if (typeof document === 'undefined') return false;
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === 'function') {
      const reduced = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      return true;
    }
  }
  return false;
};

// SIGN IN — one button, this app's own login (owner, 1 Oct 2026). It used to
// open an "As MOC Patient / As MOC Tenant" chooser; patients have their own
// site and app, reached from the separate "For patients" link, so the provider
// app's Sign in never asks who you are.
function SignInButton({ label, className, testId, onClick }) {
  return <button type="button" onClick={onClick} className={className} data-testid={testId}>{label}</button>;
}

/** The language popup, rendered once for desktop and once for the phone bar. */
function LanguagePicker({ variant, open, setOpen, languages, current, onPick, title, fallbackLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, setOpen]);

  return (
    <div ref={ref} className="relative" data-testid={`public-nav-${variant}-language-container`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="p-2 hover:bg-gray-100 rounded-lg transition cursor-pointer"
        title={title}
        aria-label={title}
        data-testid={`public-nav-${variant}-language-button`}
      >
        <Languages className="w-5 h-5 text-gray-700" />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-48 max-h-72 overflow-y-auto p-1"
          data-testid={`public-nav-${variant}-language-dropdown`}
        >
          {languages && languages.length > 0 ? (
            languages.map((langItem) => {
              const code = (langItem.code || '').toLowerCase();
              const active = current?.startsWith(code);
              return (
                <button
                  type="button"
                  key={langItem.languageId || code}
                  onClick={() => onPick(code)}
                  className={`block w-full text-left px-4 py-2 text-sm rounded-md transition cursor-pointer ${active ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                  data-testid={`public-nav-${variant}-language-option-${code}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate">{langItem.name}</span>
                    <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                  </span>
                </button>
              );
            })
          ) : (
            <button
              type="button"
              onClick={() => onPick('en')}
              className={`block w-full text-left px-4 py-2 text-sm rounded-md transition cursor-pointer ${current?.startsWith('en') ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              data-testid={`public-nav-${variant}-language-option-en`}
            >
              {fallbackLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PublicNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation('home');

  const languages = useSelector((state) => state.translation?.languages || []);
  // Doctor sign-up is open when some organisation accepts it (or the host's
  // does) — hooks/useDoctorRegistrationOpen, not the old platform flag.
  const { open: doctorRegEnabled } = useDoctorRegistrationOpen();
  const anyRegistrationEnabled = doctorRegEnabled;
  const hasPatientSite = Boolean(PATIENT_WEB_URL);

  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Home fetches nothing itself any more — the bar owns the language master
  // because the bar is the only thing that draws it.
  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the phone sheet on navigation — otherwise it covers the page the
  // visitor just asked for.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const onHome = location.pathname === '/' || location.pathname === '/home';

  const goSection = (section) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (onHome) { scrollToFirst(section.ids); return; }
    navigate('/', { state: { scrollTo: section.id } });
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  // SIGN IN: a live session lands on its role's dashboard, otherwise the
  // login page.
  const handleTenantSignIn = () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const role = localStorage.getItem('Role');
    const id = localStorage.getItem('ID');
    const validRoles = ['admin', 'doctor', 'user', 'patient'];

    if (isAuthenticated && role && id && validRoles.includes(role)) {
      const roleRoutes = {
        doctor: '/doctorDashboard',
        admin: '/adminDashboard',
        user: '/staffDashboard',
        patient: '/patientDashboard',
      };
      navigate(roleRoutes[role] || '/dashboard');
    } else {
      navigate('/login');
    }
  };

  const signInProps = {
    label: t('home.nav_sign_in'),
    onClick: handleTenantSignIn,
  };

  const langProps = {
    open: langOpen,
    setOpen: setLangOpen,
    languages,
    current: i18n.language,
    onPick: changeLanguage,
    title: t('home.select_language'),
    fallbackLabel: t('home.language_english'),
  };

  return (
    <nav
      className="fixed top-0 inset-x-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur-sm shadow-sm"
      data-testid="public-nav"
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => (onHome ? window.scrollTo({ top: 0, behavior: 'smooth' }) : navigate('/'))}
          className="flex shrink-0 cursor-pointer items-center gap-2"
          data-testid="public-nav-brand"
        >
          <img src={logo} alt="" className="h-9 w-9 object-contain" />
          <span className="flex flex-col items-start leading-none">
            <span className="whitespace-nowrap text-lg font-bold text-[#41a4a4] sm:text-xl">
              {t('home.brand_name')}
            </span>
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.13em] text-[#9DB3B1] sm:block">
              {t('home.brand_tagline')}
            </span>
          </span>
        </button>

        <div className="hidden flex-1 items-center justify-center gap-7 lg:flex" data-testid="public-nav-links">
          {PUBLIC_SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={goSection(s)}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-gray-600 transition-colors hover:text-[#2D8A8A]"
              data-testid={`public-nav-${s.id}`}
            >
              {t(s.labelKey)}
            </a>
          ))}
          {hasPatientSite && (
            <a
              href={`${PATIENT_WEB_URL}/#/`}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-gray-600 transition-colors hover:text-[#2D8A8A]"
              data-testid="public-nav-for-patients"
            >
              {t('home.nav_for_patients')} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-0 lg:gap-3">
          <div className="hidden lg:block">
            <LanguagePicker variant="desktop" {...langProps} />
          </div>
          <div className="hidden sm:block">
            <SignInButton
              {...signInProps}
              className="cursor-pointer whitespace-nowrap rounded-xl border-2 border-[#4ABDBD] px-4 py-2 text-sm font-semibold text-[#2D8A8A] transition-colors hover:bg-[#4ABDBD]/10"
              testId="public-nav-sign-in"
            />
          </div>
          {anyRegistrationEnabled && (
            <button
              type="button"
              onClick={() => navigate('/register-choice')}
              className="hidden cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md sm:inline-flex"
              data-testid="public-nav-get-started"
            >
              {t('home.nav_get_started')}
            </button>
          )}
          <div className="lg:hidden">
            <LanguagePicker variant="mobile" {...langProps} />
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t('home.close_menu') : t('home.open_menu')}
            aria-expanded={menuOpen}
            className="cursor-pointer rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 lg:hidden"
            data-testid="public-nav-menu-toggle"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-gray-100 bg-white px-4 py-4 shadow-lg lg:hidden"
          data-testid="public-nav-menu"
        >
          <div className="flex flex-col">
            {PUBLIC_SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={goSection(s)}
                className="cursor-pointer rounded-lg px-2 py-3 text-[15px] font-medium text-gray-700 transition-colors hover:bg-[#f0faf9] hover:text-[#2D8A8A]"
                data-testid={`public-nav-menu-${s.id}`}
              >
                {t(s.labelKey)}
              </a>
            ))}
            {hasPatientSite && (
              <a
                href={`${PATIENT_WEB_URL}/#/`}
                className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-2 py-3 text-[15px] font-medium text-gray-700 transition-colors hover:bg-[#f0faf9] hover:text-[#2D8A8A]"
                data-testid="public-nav-menu-for-patients"
              >
                {t('home.nav_for_patients')} <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-4">
            <SignInButton
              {...signInProps}
              className="w-full cursor-pointer rounded-xl border-2 border-[#4ABDBD] px-4 py-2.5 text-sm font-semibold text-[#2D8A8A]"
              testId="public-nav-menu-sign-in"
            />
            {anyRegistrationEnabled && (
              <button
                type="button"
                onClick={() => navigate('/register-choice')}
                className="w-full cursor-pointer rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-4 py-2.5 text-sm font-semibold text-white shadow-sm"
                data-testid="public-nav-menu-get-started"
              >
                {t('home.nav_get_started')}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
